import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { candidatesService, documentsService } from '../services';
import type {
  CreateCandidateDto,
  UpdateCandidateDto,
  CandidateFilters,
  PaginationParams,
  AddCandidateSkillDto,
  UpdateCandidateSkillDto,
  CreateExperienceDto,
  UpdateExperienceDto,
  CreateEducationDto,
  UpdateEducationDto,
} from '../types';

const CANDIDATES_KEY = 'candidates';

/**
 * Hook to fetch candidates statistics
 */
export function useCandidatesStats() {
  return useQuery({
    queryKey: [CANDIDATES_KEY, 'stats'],
    queryFn: () => candidatesService.getStats(),
  });
}

/**
 * Hook to fetch paginated candidates list
 */
export function useCandidates(filters: CandidateFilters = {}, pagination: PaginationParams = {}) {
  return useQuery({
    queryKey: [CANDIDATES_KEY, 'list', filters, pagination],
    queryFn: () => candidatesService.list(filters, pagination),
  });
}

/**
 * Hook to fetch a single candidate
 */
export function useCandidate(id: number | undefined) {
  return useQuery({
    queryKey: [CANDIDATES_KEY, 'detail', id],
    queryFn: () => candidatesService.getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook to fetch candidate with all details
 */
export function useCandidateWithDetails(id: number | undefined) {
  return useQuery({
    queryKey: [CANDIDATES_KEY, 'detail', id, 'full'],
    queryFn: () => candidatesService.getWithDetails(id!),
    enabled: !!id,
  });
}

/**
 * Hook to create a candidate
 */
export function useCreateCandidate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateCandidateDto) => candidatesService.create(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CANDIDATES_KEY] });
    },
  });
}

/**
 * Hook to update a candidate
 */
export function useUpdateCandidate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: UpdateCandidateDto }) =>
      candidatesService.update(id, dto),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [CANDIDATES_KEY] });
      queryClient.invalidateQueries({ queryKey: [CANDIDATES_KEY, 'detail', id] });
    },
  });
}

/**
 * Hook to toggle a candidate's favorite state (shared at company level)
 */
export function useToggleFavorite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isFavorite }: { id: number; isFavorite: boolean }) =>
      candidatesService.toggleFavorite(id, isFavorite),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [CANDIDATES_KEY] });
      queryClient.invalidateQueries({ queryKey: [CANDIDATES_KEY, 'detail', id] });
    },
  });
}

/**
 * Hook to delete a candidate
 */
export function useDeleteCandidate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => candidatesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CANDIDATES_KEY] });
    },
  });
}

/**
 * Hook for semantic search
 */
export function useSemanticSearch(query: string, options: Parameters<typeof candidatesService.semanticSearch>[1] = {}) {
  return useQuery({
    queryKey: [CANDIDATES_KEY, 'semantic-search', query, options],
    queryFn: () => candidatesService.semanticSearch(query, options),
    enabled: query.length >= 3,
  });
}

/**
 * Hook for finding similar candidates
 */
export function useSimilarCandidates(candidateId: number | undefined, options: Parameters<typeof candidatesService.findSimilar>[1] = {}) {
  return useQuery({
    queryKey: [CANDIDATES_KEY, 'similar', candidateId, options],
    queryFn: () => candidatesService.findSimilar(candidateId!, options),
    enabled: !!candidateId,
  });
}

/**
 * Hook to upload CV
 */
export function useUploadCV() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ candidateId, file }: { candidateId: number; file: File }) =>
      candidatesService.uploadCV(candidateId, file),
    onSuccess: (_, { candidateId }) => {
      queryClient.invalidateQueries({ queryKey: [CANDIDATES_KEY, 'detail', candidateId] });
    },
  });
}

/**
 * Hook to extract CV
 */
export function useExtractCV() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ candidateId, documentId }: { candidateId: number; documentId: number }) =>
      candidatesService.extractCV(candidateId, documentId),
    onSuccess: (_, { candidateId }) => {
      queryClient.invalidateQueries({ queryKey: [CANDIDATES_KEY, 'detail', candidateId] });
    },
  });
}

/**
 * Hook to generate embedding
 */
export function useGenerateEmbedding() {
  return useMutation({
    mutationFn: (candidateId: number) => candidatesService.generateEmbedding(candidateId),
  });
}

/**
 * Hook to add a skill to a candidate
 */
export function useAddCandidateSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ candidateId, dto }: { candidateId: number; dto: AddCandidateSkillDto }) =>
      candidatesService.addSkill(candidateId, dto),
    onSuccess: (_, { candidateId }) => {
      queryClient.invalidateQueries({ queryKey: [CANDIDATES_KEY, 'detail', candidateId] });
    },
  });
}

/**
 * Hook to update a candidate's skill
 */
export function useUpdateCandidateSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ candidateId, skillId, dto }: { candidateId: number; skillId: number; dto: UpdateCandidateSkillDto }) =>
      candidatesService.updateSkill(candidateId, skillId, dto),
    onSuccess: (_, { candidateId }) => {
      queryClient.invalidateQueries({ queryKey: [CANDIDATES_KEY, 'detail', candidateId] });
    },
  });
}

/**
 * Hook to remove a skill from a candidate
 */
export function useRemoveCandidateSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ candidateId, skillId }: { candidateId: number; skillId: number }) =>
      candidatesService.removeSkill(candidateId, skillId),
    onSuccess: (_, { candidateId }) => {
      queryClient.invalidateQueries({ queryKey: [CANDIDATES_KEY, 'detail', candidateId] });
    },
  });
}

/**
 * Hook to add an experience to a candidate
 */
export function useAddExperience() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ candidateId, dto }: { candidateId: number; dto: CreateExperienceDto }) =>
      candidatesService.addExperience(candidateId, dto),
    onSuccess: (_, { candidateId }) => {
      queryClient.invalidateQueries({ queryKey: [CANDIDATES_KEY, 'detail', candidateId] });
    },
  });
}

/**
 * Hook to update a candidate's experience
 */
export function useUpdateExperience() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ candidateId, experienceId, dto }: { candidateId: number; experienceId: number; dto: UpdateExperienceDto }) =>
      candidatesService.updateExperience(candidateId, experienceId, dto),
    onSuccess: (_, { candidateId }) => {
      queryClient.invalidateQueries({ queryKey: [CANDIDATES_KEY, 'detail', candidateId] });
    },
  });
}

/**
 * Hook to remove an experience from a candidate
 */
export function useRemoveExperience() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ candidateId, experienceId }: { candidateId: number; experienceId: number }) =>
      candidatesService.removeExperience(candidateId, experienceId),
    onSuccess: (_, { candidateId }) => {
      queryClient.invalidateQueries({ queryKey: [CANDIDATES_KEY, 'detail', candidateId] });
    },
  });
}

/**
 * Hook to add an education to a candidate
 */
export function useAddEducation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ candidateId, dto }: { candidateId: number; dto: CreateEducationDto }) =>
      candidatesService.addEducation(candidateId, dto),
    onSuccess: (_, { candidateId }) => {
      queryClient.invalidateQueries({ queryKey: [CANDIDATES_KEY, 'detail', candidateId] });
    },
  });
}

/**
 * Hook to update a candidate's education
 */
export function useUpdateEducation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ candidateId, educationId, dto }: { candidateId: number; educationId: number; dto: UpdateEducationDto }) =>
      candidatesService.updateEducation(candidateId, educationId, dto),
    onSuccess: (_, { candidateId }) => {
      queryClient.invalidateQueries({ queryKey: [CANDIDATES_KEY, 'detail', candidateId] });
    },
  });
}

/**
 * Hook to remove an education from a candidate
 */
export function useRemoveEducation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ candidateId, educationId }: { candidateId: number; educationId: number }) =>
      candidatesService.removeEducation(candidateId, educationId),
    onSuccess: (_, { candidateId }) => {
      queryClient.invalidateQueries({ queryKey: [CANDIDATES_KEY, 'detail', candidateId] });
    },
  });
}

/**
 * Hook to delete a candidate document (hard delete)
 */
export function useDeleteDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ candidateId, documentId }: { candidateId: number; documentId: number }) =>
      documentsService.delete(candidateId, documentId),
    onSuccess: (_, { candidateId }) => {
      queryClient.invalidateQueries({ queryKey: [CANDIDATES_KEY, 'detail', candidateId] });
    },
  });
}
